/**
 * <MarketDefaultApplyReceipt> — turn-anchored 3-group chat receipt for
 * `apply_market_defaults` complete-phase (market-default-apply-ux Phase 2
 * Session 2.1).
 *
 * CHROME MODEL: mirrors <TableEditReceipt> (ref #1) — section-label header +
 * status dot pill + bordered group container. Mounted ONLY via
 * <ToolResultRenderer> at apply_market_defaults complete-phase; reload
 * survival is automatic via `toolCallStates` (ADR 0002 — do NOT mount this
 * receipt outside <ToolResultRenderer>).
 *
 * 4-STATE NOTE: when mounted via <ToolResultRenderer>, `state.result` is
 * always present (success). Loading/error states are owned by the dispatcher
 * (executing-phase <MetricsCard> / tool_error -> <ToolErrorCallout>). This
 * component owns ONLY the success + degenerate-empty branches.
 *
 * N-APPLIED COUNT (ADR 0008): grounds on `applied[]` + `appliedTableCells[]`
 * (NOT `appliedSet`, which is a census). Shape-A (applied[]) entries whose
 * `inputName` starts with 'Location.' are EXCLUDED (name-based — the runtime
 * inputs map carries no dataType; see isNotLocationA). Shape-B (appliedTableCells[])
 * entries are NEVER location-filtered (table cells are never Location.* scalars).
 * Count and rendered row list share a SINGLE filtered source — they cannot drift.
 *
 * APPLIED ROWS — TWO SHIPPED SHAPES (B1, ADR 0005 gap-analysis):
 *   Shape A (applied[]):         { inputName, value, marketField, source, confidence }  NO unit NO namedRange
 *   Shape B (appliedTableCells[]): { table, rowLabel, column, namedRange, value, marketField, source, confidence }  NO inputName
 * Both adapt to a uniform { label, marketField, value } row so the row
 * renderer is shape-agnostic.
 *
 * UNIT RENDERING (B1): `fmtValue(value)` with NO unit on Applied rows — the
 * two Applied shapes carry no `unit` field. `fmtValue(value, unit)` is used
 * only for informational/unmatched rows which carry `unit?`.
 *
 * rgba() glow on the status dot has no token alias — annotated @design-token.
 * `components/builder/` is OUTSIDE audit-no-raw-hex-in-signal.sh TARGETS.
 */

import { useState } from 'react';
import { Chip } from '@/components/ui/Chip';
import type {
  ApplyMarketDefaultsResult,
  AliasMatchState,
} from '@arcanum-hq/shared-types';

// ── ADR 0007: derive item types from the exported result type, not barrel ────
// informational / genuinely-unmatched rows — carry `unit?`
type MarketFieldValueUnit =
  ApplyMarketDefaultsResult['informationalSet'][number]; // { marketField; value; unit? }
// Applied-group shape A — scalar input writes (NO unit, NO namedRange)
type AppliedInput = ApplyMarketDefaultsResult['applied'][number]; // { inputName; value; marketField; source; confidence }
// Applied-group shape B — table-cell writes (NO inputName; has namedRange + table/rowLabel/column)
type AppliedTableCell = ApplyMarketDefaultsResult['appliedTableCells'][number]; // { table; rowLabel; column; namedRange; value; marketField; source; confidence }

export interface MarketDefaultApplyReceiptProps {
  result: ApplyMarketDefaultsResult;
  /** Disables apply affordances; receipt still renders informationally. */
  readOnly?: boolean;
  conversationId: string;
  onApplyToInput: (
    field: MarketFieldValueUnit,
    aliasState: AliasMatchState
  ) => void;
}

// ── humanize / fmtValue helpers — ported from prototype:224-237 ─────────────

/** snake_case → Title Case */
function humanize(field: string): string {
  return field
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Format a numeric value with an optional unit suffix.
 * Used ONLY for informational/unmatched rows that carry `unit?`.
 * Applied rows call fmtValue(value) with no unit argument (B1 — those shapes have no unit).
 */
function fmtValue(v: number, unit?: string): string {
  const n = v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (!unit) return n;
  if (unit === '%') return `${n}%`;
  if (unit.startsWith('$')) return `$${n}${unit.slice(1)}`;
  if (unit === '$') return `$${n}`;
  return `${n} ${unit}`;
}

// ── Location-exclusion predicate (ADR 0008) — shape A only ──────────────────
// NAME-BASED: geocoded writes land on `Location.*` dotted sub-attributes, which
// this catches. A `dataType==='location'` arm was dropped because the runtime
// `inputs` map (`useConversationViews().data.inputs` = `ProvenanceRecord`)
// carries NO `dataType` field — that arm was unreachable in production and a
// unit test with a synthetic `dataType` fixture gave false assurance (gap-analysis
// code IMPORTANT-2). The apply partition never carries a bare location input
// anyway (market defaults write cap_rate/rent/etc.), so name-based realizes the
// ADR-0008 intent. The teaser count (builder.tsx marketApplyCounts) is name-based
// too — keeping the two paths provably identical (MINOR-3).
function isNotLocationA(r: AppliedInput): boolean {
  return !r.inputName.includes('Location.');
}

// ── Two-shape Applied row adapter (B1) ──────────────────────────────────────

interface AppliedRowData {
  key: string;
  label: string;
  marketField: string;
  value: number;
  // Provenance-honest source tag from the apply tool (extractSourceTag):
  // 'FRED' | 'HUD' | 'RentCast' | 'BLS' = genuine live market data;
  // 'Static' = the market lookup found nothing for this metro and fell back to
  // a static default. Drives the per-row chip so the receipt matches the
  // Variables panel (which honestly shows static-default fields as `system`,
  // NOT `market`). Without this, the receipt hard-labeled every applied row
  // "market" even when the value was a static default (provenance dishonesty).
  source: string;
}

function toAppliedRowFromInput(r: AppliedInput): AppliedRowData {
  return {
    key: r.inputName,
    // shape A: humanize the inputName (not table-cell composite, not verbatim)
    label: humanize(r.inputName),
    marketField: r.marketField,
    value: r.value,
    source: r.source,
  };
}

function toAppliedRowFromTableCell(r: AppliedTableCell): AppliedRowData {
  const composite = `${r.table} · ${r.rowLabel} · ${r.column}`;
  return {
    // Use || not ?? so empty-string namedRange falls through to composite (same as absent)
    key: r.namedRange || `${r.table}.${r.rowLabel}.${r.column}`,
    // shape B: namedRange verbatim, or composite when absent/empty (prototype:455)
    label: r.namedRange || composite,
    marketField: r.marketField,
    value: r.value,
    source: r.source,
  };
}

// A row is genuinely market-sourced unless the apply tool tagged it 'Static'
// (static-default fallback — no live market data for this metro). Mirrors the
// Variables panel's `default -> 'system'` chip mapping (VariableRow.tsx).
function isStaticFallback(source: string): boolean {
  return source.toLowerCase() === 'static';
}

// ── Sub-components ───────────────────────────────────────────────────────────

function GroupHeader({
  label,
  count,
  tone,
}: {
  label: string;
  count: number;
  tone: 'accent' | 'warn';
}) {
  return (
    <div className="flex items-center justify-between px-3 py-1.5 border-b border-panel-border bg-bg/30">
      <span className="flex items-center gap-1.5">
        <span
          className={`w-1 h-1 rounded-full shrink-0 ${
            tone === 'accent' ? 'bg-accent' : 'bg-warn'
          }`}
        />
        <span
          className={`text-[8px] font-mono font-semibold tracking-[2px] uppercase ${
            tone === 'accent' ? 'text-text-muted' : 'text-warn'
          }`}
        >
          {label}
        </span>
      </span>
      <span className="text-[9px] font-mono tabular-nums text-text-dim">
        {count}
      </span>
    </div>
  );
}

function AppliedRow({ row }: { row: AppliedRowData }) {
  // Provenance-honest chip: a static-default fallback shows `system` (matching
  // the Variables panel), NOT `market`. Only genuine live market data
  // (FRED/HUD/RentCast/BLS) gets the `market` chip.
  const staticFallback = isStaticFallback(row.source);
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-1.5">
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-[11px] font-mono text-text-bright truncate">
          {row.label}
        </span>
        <span className="text-text-dim text-[10px] font-mono">&larr;</span>
        <span className="text-[10px] font-mono text-text-muted truncate">
          {humanize(row.marketField)}
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {/* B1: NO unit on Applied rows — fmtValue(value) with no unit arg */}
        <span className="text-[11px] font-mono tabular-nums text-accent">
          {fmtValue(row.value)}
        </span>
        {staticFallback ? (
          <Chip variant="system" label="system" />
        ) : (
          <Chip variant="market" label="market" />
        )}
      </div>
    </div>
  );
}

function UnmatchedRow({
  field,
  readOnly,
  onApply,
}: {
  field: MarketFieldValueUnit;
  readOnly: boolean;
  onApply: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-1.5">
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-[11px] font-mono text-text-bright truncate">
          {humanize(field.marketField)}
        </span>
        <span className="text-[11px] font-mono tabular-nums text-warn">
          {fmtValue(field.value, field.unit)}
        </span>
      </div>
      {/* PROMINENT affordance — ADR 0003 */}
      <button
        type="button"
        disabled={readOnly}
        onClick={onApply}
        data-testid={`unmatched-apply-${field.marketField}`}
        className="text-[10px] font-mono text-accent hover:text-accent/80 border border-accent/40 hover:border-accent/60 rounded-[2px] px-2 py-0.5 cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
      >
        Apply to an input&hellip;
      </button>
    </div>
  );
}

function InformationalRow({
  field,
  readOnly,
  onApply,
}: {
  field: MarketFieldValueUnit;
  readOnly: boolean;
  onApply: () => void;
}) {
  return (
    <div className="group flex items-center justify-between gap-3 px-3 py-1 hover:bg-accent-faint transition-colors">
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-[10px] font-mono text-text-muted truncate">
          {humanize(field.marketField)}
        </span>
        <span className="text-[10px] font-mono tabular-nums text-text-dim">
          {fmtValue(field.value, field.unit)}
        </span>
      </div>
      {/* LOW-EMPHASIS affordance — hover-revealed (ADR 0003 primary entry) */}
      <button
        type="button"
        disabled={readOnly}
        onClick={onApply}
        data-testid={`informational-apply-${field.marketField}`}
        className="text-[9px] font-mono text-text-dim group-hover:text-accent transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed shrink-0 opacity-0 group-hover:opacity-100"
      >
        apply to an input&hellip;
      </button>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function MarketDefaultApplyReceipt({
  result,
  readOnly = false,
  onApplyToInput,
}: MarketDefaultApplyReceiptProps) {
  const [showInformational, setShowInformational] = useState(false);

  // ── N-applied reconciliation (ADR 0008) ─────────────────────────────────
  // Single filtered source — count CANNOT drift from itemization.
  const appliedInputRows: AppliedRowData[] = (result.applied ?? [])
    .filter(isNotLocationA)
    .map(toAppliedRowFromInput);

  // Shape B: table-cell rows are NEVER location-filtered (see note 2 in blueprint)
  const appliedTableCellRows: AppliedRowData[] = (
    result.appliedTableCells ?? []
  ).map(toAppliedRowFromTableCell);

  const allAppliedRows = [...appliedInputRows, ...appliedTableCellRows];
  const appliedCount = allAppliedRows.length;

  const hasUnmatched = (result.genuinelyUnmatchedSet ?? []).length > 0;
  const hasInformational = (result.informationalSet ?? []).length > 0;

  // ── Degenerate-empty branch (4-state note: only success + degenerate-empty) ─
  const isEmpty = appliedCount === 0 && !hasUnmatched && !hasInformational;

  return (
    <div
      className="mb-5 max-w-[72%] ml-8"
      data-testid="market-default-apply-receipt"
    >
      {/* ── Receipt header: section label + Live chip + status dot pill ── */}
      <div className="flex items-center justify-between mb-2.5">
        <span className="flex items-center gap-2">
          <span className="text-[9px] font-mono font-semibold tracking-[1.2px] uppercase text-text-muted">
            Market defaults &middot; {result.metro}
          </span>
          <span className="text-[8px] font-mono font-semibold tracking-[0.8px] uppercase text-accent border border-accent/40 rounded-[2px] px-1 py-0.5 leading-none">
            Live
          </span>
        </span>
        {!isEmpty && (
          <span className="flex items-center gap-1.5 text-[10px] font-mono text-accent">
            <span
              className="w-1.5 h-1.5 rounded-full bg-accent shrink-0"
              style={{ boxShadow: '0 0 4px rgba(0,200,83,0.4)' }} // @design-token glow has no token
            />
            {appliedCount} applied
          </span>
        )}
      </div>

      {/* ── Degenerate-empty branch ── */}
      {isEmpty && (
        <div
          className="rounded-[2px] border border-panel-border bg-panel-bg px-3 py-2.5 text-center"
          data-testid="receipt-empty"
        >
          <span className="text-[11px] font-mono text-text-muted">
            No market values were available for {result.metro}.
          </span>
        </div>
      )}

      {/* ── Success: the 3 groups ── */}
      {!isEmpty && (
        <div className="rounded-[3px] border border-panel-border bg-panel-bg overflow-hidden">
          {/* GROUP 1 — Applied */}
          <GroupHeader label="Applied" count={appliedCount} tone="accent" />
          <div className="divide-y divide-panel-border/40">
            {allAppliedRows.map((row) => (
              <AppliedRow key={row.key} row={row} />
            ))}
          </div>

          {/* GROUP 2 — Couldn't auto-match (conditional, ADR 0003 graceful-empty) */}
          {hasUnmatched && (
            <>
              <GroupHeader
                label="Couldn't auto-match"
                count={(result.genuinelyUnmatchedSet ?? []).length}
                tone="warn"
              />
              <div className="divide-y divide-panel-border/40">
                {(result.genuinelyUnmatchedSet ?? []).map((f) => (
                  <UnmatchedRow
                    key={f.marketField}
                    field={f}
                    readOnly={readOnly}
                    onApply={() => onApplyToInput(f, 'genuinely_unmatched')}
                  />
                ))}
              </div>
            </>
          )}

          {/* GROUP 3 — Also pulled · informational (collapsed, de-emphasized) */}
          {hasInformational && (
            <>
              <button
                type="button"
                onClick={() => setShowInformational((v) => !v)}
                data-testid="receipt-informational-toggle"
                className="w-full flex items-center justify-between px-3 py-1.5 bg-bg/40 hover:bg-bg/70 transition-colors cursor-pointer border-t border-panel-border"
              >
                <span className="flex items-center gap-1.5 text-[8px] font-mono font-semibold tracking-[2px] uppercase text-text-dim">
                  <span className="text-text-muted">
                    {showInformational ? '▾' : '▸'}
                  </span>
                  Also pulled &middot; informational
                </span>
                <span className="text-[9px] font-mono tabular-nums text-text-dim">
                  {(result.informationalSet ?? []).length}
                </span>
              </button>
              {showInformational && (
                <div
                  className="divide-y divide-panel-border/30"
                  data-testid="receipt-informational-rows"
                >
                  {(result.informationalSet ?? []).map((f) => (
                    <InformationalRow
                      key={f.marketField}
                      field={f}
                      readOnly={readOnly}
                      onApply={() =>
                        onApplyToInput(f, 'intentionally_unmapped')
                      }
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
